import type { LucideIcon } from "lucide-react";
import React, { useEffect, useState, useCallback } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetContentHighPriority,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, ChevronLeft, FileSearch, Lock, Plus, User, Wrench, Shield, FileText, Pencil, Mail, CreditCard, Columns3, Table2, CheckCircle2, Clock, Target, MessageSquare, AlertTriangle, Copy, RotateCcw, XCircle, ChevronDown, PenLine, Smartphone, ArrowLeftRight, FolderOpen, Search, CalendarCheck } from "lucide-react";
import { AgendarTarefaPopover } from "@/components/whatsapp-inbox/AgendarTarefaPopover";
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useItemDifereDaProposta } from '@/hooks/useItemDifereDaProposta';
import { RecuperarItemModal } from "./RecuperarItemModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TimelineUnificada } from "@/components/timeline/TimelineUnificada";
import { StatusBadgeInteractive } from "@/components/pedido/shared/StatusBadgeInteractive";
import ItensTab from "./tabs/ItensTab";
import { ConvergenciaValidacaoBko } from "./ConvergenciaValidacaoBko";
import { BotaoRevisarProposta } from "./BotaoRevisarProposta";
import AnexosTab from "./tabs/AnexosTab";
import ClienteTab from "./tabs/ClienteTab";
import { PropostaOrigemPedido } from "./PropostaOrigemPedido";

import SLAView from "./tabs/SLAView";
import { formatPedidoCode } from "@/lib/formatters";
import { formatarCNPJ, formatarCPF, keepOnlyDigits } from "@/utils/formatters";
import { usePedidoDetail } from "@/hooks/usePedidoDetail";
import { usePedidoConversa } from "@/hooks/usePedidoConversa";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from '@/integrations/supabase/types';

import { PedidoCard } from "./PedidoCard";
import { EditarItemCompletoModal } from "./modals";
import { useToast } from "@/hooks/use-toast";
import { useObrigatoriedadesItem } from "@/hooks/useObrigatoriedadesItem";
import { validarItem } from "@/services/validacaoItemService";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatPhoneForCall } from "@/utils/phone";
import { useUpdatePedido } from "@/hooks/useUpdatePedido";
import { cn } from "@/lib/utils";
import { getQuadroLabel } from "@/lib/quadro-utils";
import { usePendingSavesContext } from "@/contexts/PendingSavesContext";
import { useHasRole, useHasAnyRole } from "@/hooks/useHasRole";
import { EmailClienteDialog } from "@/components/email-cliente/EmailClienteDialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
type QuadroEnum = Database['public']['Enums']['quadro_enum'];

interface PainelPedidoProps {
  pedidoId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  origin?: "kanban" | "planilha" | "visao360" | null;
  focusItemSeq?: number;
  highPriority?: boolean;
}

interface PedidoData {
  id: string;
  pedido_id: string;
  numero_pedido: number;
  cnpj: string;
  razao_social: string;
  consultor_nome?: string;
  total_venda_nova?: number;
  total_migracao?: number;
  total_faturamento?: number;
  quadro_atual?: string;
  coluna_atual?: string;
  substatus_nome?: string;
  status_cor?: string;
  substatus_id?: string;
  gestor_nome?: string;
  gestor_telefone?: string;
  gestor_whatsapp?: string;
  gestor_email?: string;
  gestor_rg?: string;
  gestor_cpf?: string;
  cessionario_nome?: string;
  cessionario_telefone?: string;
  cessionario_whatsapp?: string;
  cessionario_email?: string;
  cessionario_rg?: string;
  cessionario_cpf?: string;
  data_referencia?: string;
  qtd_produtos?: number;
  cliente_id?: string;
  lead_origem_id?: string | null; //  NOVO: ID do lead origem para proposta
  dono_carteira_nome?: string;
  dono_carteira_equipe?: string;
}

/**
 * Chip discreto e clicável que mostra um valor (CNPJ/Pedido) e copia ao clicar.
 * Padrão visual do painel 360: outline sutil, mono, ícone de copiar no hover.
 */
function CopyChip({ label, display, copyValue, onCopy }: { label: string; display: string; copyValue: string; onCopy: (valor: string, label: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onCopy(copyValue, label)}
      title={`Copiar ${label}: ${copyValue}`}
      className="group inline-flex h-10 items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 text-xs text-white transition-colors hover:bg-white/15"
    >
      <span className="text-white/60">{label}</span>
      <span className="font-semibold tabular-nums text-white">{display}</span>
      <Copy className="h-3.5 w-3.5 text-white/55 group-hover:text-white" />
    </button>
  );
}

export const PainelPedido = React.memo(function PainelPedido({ pedidoId, open, onOpenChange, origin, focusItemSeq, highPriority = false }: PainelPedidoProps) {
  const { data: pedido, isLoading: loading, error: queryError, refetch } = usePedidoDetail(pedidoId, open);
  const { data: conversaDoPedido, isLoading: carregandoConversa } = usePedidoConversa(pedidoId, open);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Copiar CNPJ (só números) / número do pedido para a área de transferência
  const copiar = useCallback((valor: string, label: string) => {
    if (!valor) return;
    navigator.clipboard?.writeText(valor);
    toast({ title: `${label} copiado`, description: valor });
  }, [toast]);
  const { moveCard, isMoving } = usePedidoMovement();
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem("painelPedido:lastTab");
    // 'cliente' e 'sla' foram removidas; cai pra 'itens'.
    return saved && saved !== 'cliente' && saved !== 'sla' ? saved : "itens";
  });
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);
  const [recuperarCancId, setRecuperarCancId] = useState<string | null>(null);
  const [cancelamentoAberto, setCancelamentoAberto] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  
  // ✅ Contexto de salvamentos pendentes (guard de navegação)
  const pendingSaves = usePendingSavesContext();
  const [showPendingWarning, setShowPendingWarning] = useState(false);
  const [isFlushing, setIsFlushing] = useState(false);
  
  const updatePedidoMutation = useUpdatePedido();
  const isMaster = useHasRole('master'); //  SOMENTE master pode deletar permanentemente
  const podeEnviarEmail = useHasAnyRole(['master', 'diretor', 'gerente', 'coordenador', 'supervisor', 'bko', 'qualidade']);
  const [emailAberto, setEmailAberto] = useState(false);
  const [observacoes, setObservacoes] = useState(pedido?.observacoes || '');
  
  useEffect(() => {
    if (pedido?.observacoes) {
      setObservacoes(pedido.observacoes);
    }
  }, [pedido?.observacoes]);

  // ✅ REFETCH ao abrir (25/06): garante dados frescos. Recuperar/revisar muda o pedido no
  // banco e o painel precisa refletir NA HORA (antes mostrava cache CANCELADO desatualizado).
  useEffect(() => {
    if (open && pedidoId) { refetch(); }
  }, [open, pedidoId, refetch]);

  const handleObservacoesBlur = async () => {
    if (observacoes === (pedido?.observacoes || '')) return;
    
    await updatePedidoMutation.mutateAsync({
      pedidoId: pedidoId,
      updates: { observacoes }
    });
  };

  const clienteId = pedido?.cliente_id || "";
  const error = queryError ? (queryError as Error).message : null;
  // Distingue "limitação de visão" (RLS: pedido fora da carteira/equipe do usuário)
  // de um erro REAL do sistema. O primeiro não deve assustar — é só permissão, não quebra.
  const semAcesso = !!error && /sem permiss|n[aã]o encontrado/i.test(error);


  // Mutation para deletar item
  const deleteItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await supabase
        .from("itens_pedido")
        .delete()
        .eq("id", itemId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Item removido com sucesso" });
      queryClient.invalidateQueries({ queryKey: ["itens_pedido", pedidoId] });
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["pedido-itens-completo", pedidoId] });
      queryClient.invalidateQueries({ queryKey: ["pedido-detail", pedidoId] });
    },
    onError: (error: Error) => {
      toast({ 
        variant: "destructive", 
        title: "Erro ao remover item",
        description: error.message 
      });
    }
  });

  // Buscar statuses disponíveis do quadro atual (do primeiro item)
  const primeiroItem = pedido?.itens?.[0];
  const { data: statusesDisponiveis = [] } = useQuery({
    queryKey: ['status-catalogo', primeiroItem?.quadro_atual],
    queryFn: async () => {
      if (!primeiroItem?.quadro_atual) return [];
      const { data } = await supabase
        .from('status_catalogo')
        .select('*')
        .eq('quadro', primeiroItem.quadro_atual as any)
        .eq('ativo', true)
        .order('ordem');
      return data || [];
    },
    enabled: !!primeiroItem?.quadro_atual && open
  });

  // ✅ Query para buscar motivos de cancelamento
  const todosCancelados = pedido?.itens?.length > 0 && pedido.itens.every((i: any) => i.quadro_atual === 'PEDIDO_CANCELADO');
  const itemIds = pedido?.itens?.map((i: any) => i.id) || [];
  
  // Roda sempre que o painel está aberto e há itens — NÃO depende de "todos
  // cancelados" (antes, qualquer borda nisso escondia o motivo do cancelamento).
  const { data: cancelamentos = [] } = useQuery({
    queryKey: ['cancelamentos-pedido', pedidoId],
    queryFn: async () => {
      if (itemIds.length === 0) return [];
      const { data, error } = await supabase
        .from('item_cancelamentos')
        .select('id, item_id, recuperado, data_limite_recuperacao, observacao, cancelado_em, snapshot_anterior, motivo:motivos_cancelamento(titulo, codigo), cancelado_por_usuario:usuarios!item_cancelamentos_cancelado_por_fkey(nome)')
        .in('item_id', itemIds)
        .order('cancelado_em', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: open && itemIds.length > 0,
    // Painel operacional abre SEMPRE fresco (Douglas 07/08) — o global é
    // refetchOnMount:false e o selo "Recuperado" ficava velho após o recover.
    refetchOnMount: 'always',
    staleTime: 10_000,
  });
  const itemRecuperavelId = (cancelamentos as { id?: string; item_id?: string; recuperado?: boolean | null }[])
    .find((c) => !c.recuperado && c.id && c.item_id)?.item_id;
  const { data: diferencaProposta } = useItemDifereDaProposta(itemRecuperavelId);
  const recuperarBloqueado = diferencaProposta?.difere === true;

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    localStorage.setItem("painelPedido:lastTab", value);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onOpenChange(false);
      }
      if (e.key === "i" && e.ctrlKey) {
        e.preventDefault();
        setActiveTab("itens");
      }
      if (e.key === "a" && e.ctrlKey) {
        e.preventDefault();
        setActiveTab("anexos");
      }
    };

    if (open) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, onOpenChange]);


  // Função para navegar ao Kanban do pedido (baseado no primeiro item)
  const handleGoToKanban = () => {
    const itemComQuadro = pedido?.itens?.find(i => i.quadro_atual && i.quadro_atual !== 'PEDIDO_CANCELADO');
    const itemFallback = pedido?.itens?.[0];
    const itemRef = itemComQuadro || itemFallback;
    if (!itemRef?.quadro_atual) {
      toast({
        title: 'Erro',
        description: 'Não foi possível determinar o quadro do pedido (sem itens)',
        variant: 'destructive'
      });
      return;
    }
    
    const quadroMap: Record<string, string> = {
      'COMERCIAL': 'comercial',
      'CONTRATO_PRIME': 'contrato-prime',
      'TRAMITACAO_VIVO': 'tramitacao-vivo',
      'TRAMITACAO_PRIME': 'tramitacao-prime',
      'QUALIDADE': 'qualidade',
      'PEDIDO_CANCELADO': 'cancelado',
    };
    
    const quadroSlug = quadroMap[itemRef.quadro_atual] || 'comercial';
    const colunaParam = itemRef.coluna_atual 
      ? `&coluna=${encodeURIComponent(itemRef.coluna_atual)}` 
      : '';
    
    onOpenChange(false);
    navigate(`/kanbans/${quadroSlug}?highlight=${pedidoId}${colunaParam}`);
  };

  // Abre a conversa de origem do pedido (nova aba — BKO analisa sem sair do
  // pedido). A conversa vem gravada no pedido desde que ele nasce.
  const handleGoToWhatsApp = () => {
    if (conversaDoPedido) {
      window.open(`/whatsapp-inbox?conversation=${conversaDoPedido}`, '_blank');
      return;
    }
    toast({
      title: carregandoConversa ? 'Carregando a conversa do pedido' : 'Pedido sem conversa',
      description: carregandoConversa
        ? 'Tente de novo em um instante.'
        : 'Este pedido nasceu sem conversa no inbox: a proposta não saiu pelo WhatsApp do CRM e o lead não tem conversa.',
    });
  };

  // Função para navegar à Planilha do pedido
  const handleGoToPlanilha = () => {
    onOpenChange(false);
    setTimeout(() => {
      navigate(`/planilha?highlight=${pedidoId}`);
    }, 300);
  };

  const ContentComponent = highPriority ? SheetContentHighPriority : SheetContent;

  // ✅ GUARD: Interceptar fechamento do Sheet
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen && pendingSaves.hasAnyPending()) {
      console.log('⚠️ [PainelPedido] Tentativa de fechar com salvamentos pendentes');
      setShowPendingWarning(true);
      return; // Não fechar ainda
    }
    onOpenChange(newOpen);
  }, [onOpenChange, pendingSaves]);

  // ✅ Forçar flush e depois fechar
  const handleFlushAndClose = useCallback(async () => {
    setIsFlushing(true);
    try {
      const result = await pendingSaves.flushAll();
      
      if (result.success) {
        toast({ title: 'Dados salvos com sucesso'});
        setShowPendingWarning(false);
        onOpenChange(false);
      } else {
        toast({ 
          variant: 'destructive',
          title: 'Erro ao salvar',
          description: result.errors.join(', ')
        });
      }
    } catch (err) {
      toast({ 
        variant: 'destructive',
        title: 'Erro ao salvar',
        description: (err as Error).message
      });
    } finally {
      setIsFlushing(false);
    }
  }, [pendingSaves, onOpenChange, toast]);

  // ✅ Fechar sem salvar (perder alterações)
  const handleDiscardAndClose = useCallback(() => {
    pendingSaves.discardAll();
    setShowPendingWarning(false);
    onOpenChange(false);
  }, [pendingSaves, onOpenChange]);

  return (
    <>
      <Sheet key={pedidoId} open={open} onOpenChange={handleOpenChange}>
        <ContentComponent 
          side="right" 
          className="flex w-full flex-col overflow-hidden sm:max-w-4xl"
          onClick={(e) => {
            const target = e.target as HTMLElement;
            
            // ✅ Se clicou em um botão ou dentro de um botão, NÃO fazer nada
            const isInsideButton = target.closest('button');
            const isInsideDropdown = target.closest('[role="menu"]');
            
            if (isInsideButton || isInsideDropdown) {
              return; //  Deixar o evento fluir para o botão/dropdown
            }
            
            console.log('🖱️ SHEET CONTENT CLICADO:', {
              target: target.tagName,
              className: target.className,
              timestamp: new Date().toISOString()
            });
          }}
        >
          <SheetHeader className="flex-shrink-0 border-b border-[#4b0712] bg-gradient-to-br from-[#4d0713] via-[#68101e] to-[#8e172b] px-6 py-5 text-white shadow-[0_12px_32px_-24px_rgba(63,5,16,.95)]">
            <div className="space-y-4">
              <div className="flex min-w-0 items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 rounded-xl text-white/80 hover:bg-white/10 hover:text-white"
                  onClick={() => onOpenChange(false)}
                  title="Voltar"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <SheetTitle className="min-w-0 truncate text-xl font-semibold tracking-tight text-white">
                  {pedido?.razao_social || "Cliente"}
                </SheetTitle>
                <span className="shrink-0 text-lg font-semibold text-white/70">
                  {pedido?.numero_pedido ? formatPedidoCode(pedido.numero_pedido) : '#P...'}
                </span>
              </div>

              {(pedido?.cpf || pedido?.cnpj || pedido?.numero_pedido != null) && (
                <div className="flex flex-wrap items-center gap-2">
                  {(pedido?.tipo_pessoa === 'PF' ? pedido?.cpf : pedido?.cnpj) && (
                    <CopyChip
                      label={pedido.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'}
                      display={pedido.tipo_pessoa === 'PF' ? formatarCPF(pedido.cpf) : formatarCNPJ(pedido.cnpj)}
                      copyValue={keepOnlyDigits(pedido.tipo_pessoa === 'PF' ? pedido.cpf || '' : pedido.cnpj)}
                      onCopy={copiar}
                    />
                  )}
                  {pedido?.numero_pedido != null && (
                    <CopyChip
                      label="Pedido"
                      display={formatPedidoCode(pedido.numero_pedido)}
                      copyValue={String(pedido.numero_pedido)}
                      onCopy={copiar}
                    />
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-medium text-white">
                {pedido?.consultor_nome && (
                  <span className="inline-flex items-center gap-1.5 border-r border-white/20 pr-3">
                    <User className="h-3.5 w-3.5 text-white/65" />
                    {pedido.consultor_nome}
                  </span>
                )}
                {pedido?.dono_carteira_nome && (
                  <span className="inline-flex items-center gap-1.5 border-r border-white/20 pr-3">
                    <Shield className="h-3.5 w-3.5 text-white/65" />
                    Dono · {pedido.dono_carteira_nome}{pedido.dono_carteira_equipe ? ` / ${pedido.dono_carteira_equipe}` : ''}
                  </span>
                )}
                {(pedido?.bko_input_nome || pedido?.bko_aceite_nome || pedido?.bko_criacao_nome) && (
                  <span className="inline-flex items-center gap-1.5 border-r border-white/20 pr-3">
                    <Wrench className="h-3.5 w-3.5 text-white/65" />
                    BKO · {pedido?.bko_input_nome || pedido?.bko_aceite_nome || pedido?.bko_criacao_nome}
                  </span>
                )}
                {pedido?.owner_bko_nome && (
                  <span className="inline-flex items-center gap-1.5 border-r border-white/20 pr-3">
                    <Target className="h-3.5 w-3.5 text-white/65" />
                    Atendimento · {pedido.owner_bko_nome}
                  </span>
                )}
                {/* O selo de origem (PRIME NOVO / PRIME CLARO / PRIME VIVO)
                    SAIU do cabeçalho — Douglas, 17/09/2026: "este prime novo
                    não precisa, pode retirar no código". Ele ocupava a ponta
                    direita da faixa e não muda nada do que o operador faz aqui.
                    ATENÇÃO: procurar por "PRIME NOVO" no código NÃO acha: o rótulo
                    nasce de `cliente_origem_prime.replaceAll('_',' ')`. Foi por
                    isso que eu disse ao Douglas que não existia — e existia, na
                    tela dele. Texto montado em runtime não se acha por grep do
                    texto final. */}
                {pedido?.cliente_is_base && (
                  <span className="inline-flex h-8 items-center rounded-lg bg-white/10 px-3 text-[11px] text-white/85">Base</span>
                )}
                {pedido?.sla_dias !== undefined && (
                  pedido.sla_dias > 0 ? (
                    <span className="inline-flex items-center gap-1.5 text-[#ffc0c6]">
                      <Clock className="h-3.5 w-3.5" />
                      {pedido.sla_dias} {pedido.sla_dias === 1 ? 'dia' : 'dias'} em atraso
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[#99e6bf]">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      No prazo
                    </span>
                  )
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-white/15 pt-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleGoToKanban}
                  className="h-10 gap-2 rounded-xl border border-white bg-white px-4 text-xs font-semibold text-[#4d0713] shadow-none hover:bg-white/90"
                  title="Ver no Kanban"
                >
                  <Columns3 className="h-4 w-4" />
                  Kanban
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGoToPlanilha}
                  className="h-10 gap-2 rounded-xl border-white/35 bg-transparent px-4 text-xs font-semibold text-white shadow-none hover:bg-white/10 hover:text-white"
                  title="Ver na Planilha"
                >
                  <Table2 className="h-4 w-4" />
                  Planilha
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGoToWhatsApp}
                  className="h-10 gap-2 rounded-xl border-white/35 bg-transparent px-4 text-xs font-semibold text-white shadow-none hover:bg-white/10 hover:text-white"
                  title="Abrir a conversa do inbox que originou esta venda (nova aba)"
                >
                  <MessageSquare className="h-4 w-4" />
                  Conversa
                </Button>
                {podeEnviarEmail && pedido?.tipo_pessoa !== 'PF' && pedido?.cnpj && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEmailAberto(true)}
                    className="h-10 gap-2 rounded-xl border-white/35 bg-transparent px-4 text-xs font-semibold text-white shadow-none hover:bg-white/10 hover:text-white"
                    title="Escrever um e-mail sobre este cliente pela atendimento@ (fica salvo na timeline)"
                  >
                    <Mail className="h-4 w-4" />
                    E-mail
                  </Button>
                )}
                {pedido?.cnpj && (
                  <AgendarTarefaPopover
                    pedidoId={pedidoId}
                    clienteId={pedido?.cliente_id ?? null}
                    leadId={pedido?.lead_origem_id ?? null}
                    cnpj={pedido.cnpj}
                    clienteNome={pedido?.razao_social ?? null}
                    ligarConversa
                    sobrePainel
                    trigger={
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10 gap-2 rounded-xl border-white/35 bg-transparent px-4 text-xs font-semibold text-white shadow-none hover:bg-white/10 hover:text-white"
                        title="Abrir uma tarefa para este cliente (a mesma Nova tarefa do Painel CRM)"
                      >
                        <CalendarCheck className="h-4 w-4" />
                        Tarefa
                      </Button>
                    }
                  />
                )}

                {(origin || pedido?.origem_lead) && (
                  <div className="ml-auto flex flex-wrap items-center gap-1.5 text-xs text-white/65">
                    {origin && (
                      <><span>Abertura:</span><strong className="font-semibold text-white">{origin === "kanban" ? "Kanban" : origin === "planilha" ? "Planilha" : "Visão 360"}</strong></>
                    )}
                    {pedido?.origem_lead && (
                      <>
                        {origin && <span>·</span>}
                        <span>Lead:</span>
                        {(() => {
                          const ORIGEM: Record<string, { Icone: LucideIcon; label: string }> = {
                            TABULACAO: { Icone: Target, label: 'Tabulação (Discadora)' },
                            MANUAL: { Icone: PenLine, label: 'Manual' },
                            PORTABILIDADE: { Icone: Smartphone, label: 'Portabilidade' },
                            RENOVACAO: { Icone: RotateCcw, label: 'Renovação' },
                            TT: { Icone: ArrowLeftRight, label: 'Troca de Titularidade' },
                            LEAD_CARTEIRA: { Icone: FolderOpen, label: 'Carteira de Clientes' },
                            PROSPECCAO: { Icone: Search, label: 'Prospecção' },
                          };
                          const o = ORIGEM[pedido.origem_lead as string];
                          return <strong className="inline-flex items-center gap-1 font-semibold text-white">{o ? <o.Icone className="h-3.5 w-3.5" /> : null}{o ? o.label : pedido.origem_lead}</strong>;
                        })()}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </SheetHeader>

        {/* §4: só ESTE bloco rola. O cabeçalho acima fica fixo — antes o
            `overflow-y-auto` estava no SheetContent e levava o cabeçalho
            junto, tirando da tela de que pedido se está falando. */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-[#f8f5f1] px-5 pb-5 dark:bg-background">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          semAcesso ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4 mt-6">
              <Lock className="h-16 w-16 text-muted-foreground" />
              <div className="text-center max-w-md">
                <h3 className="font-semibold text-lg text-foreground">Pedido fora da sua visão</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Você não tem acesso a este pedido — ele está na carteira/equipe de outra pessoa.
                  Isso <strong>não é um erro</strong> do sistema, é só uma limitação de visão por permissão.
                </p>
              </div>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Fechar
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 space-y-4 mt-6">
              <FileSearch className="h-16 w-16 text-destructive" />
              <div className="text-center max-w-md">
                <h3 className="font-semibold text-lg text-destructive">Erro ao carregar pedido</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  {error}
                </p>
              </div>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Fechar
              </Button>
            </div>
          )
        ) : !loading && !error && pedido ? (
          <div className="mt-4 space-y-4">
            {/* Banner de Cancelamento — sempre que houver QUALQUER item cancelado */}
            {(pedido?.itens || []).some((i: any) => i.quadro_atual === 'PEDIDO_CANCELADO') && (() => {
              type ItemBanner = { id?: string; seq?: number | null; quadro_atual?: string; produto?: string; plano_nome?: string };
              type CancelamentoBanner = {
                id?: string; item_id?: string; recuperado?: boolean | null; observacao?: string | null;
                cancelado_em?: string; data_limite_recuperacao?: string | null;
                snapshot_anterior?: { quadro_atual?: string; coluna_atual?: string } | null;
                motivo?: { titulo?: string; codigo?: string } | null;
                cancelado_por_usuario?: { nome?: string } | null;
              };
              const cancs = cancelamentos as unknown as CancelamentoBanner[];
              const itensCancelados = ((pedido?.itens || []) as ItemBanner[]).filter((i) => i.quadro_atual === 'PEDIDO_CANCELADO');
              const motivosUnicos = [...new Map(
                cancs
                  .filter((c) => c.motivo?.titulo)
                  .map((c) => [c.motivo?.codigo, c.motivo?.titulo as string])
              ).values()];
              const cancComObs = cancs.find((c) => c.observacao?.trim());
              const snap = cancs[0]?.snapshot_anterior || {};
              const ondeMorreu = [snap.quadro_atual ? getQuadroLabel(snap.quadro_atual) : null, snap.coluna_atual].filter(Boolean).join(' › ');
              const autorObs = cancComObs?.cancelado_por_usuario?.nome;
              const quandoObs = cancComObs?.cancelado_em ? format(new Date(cancComObs.cancelado_em), "dd/MM HH:mm", { locale: ptBR }) : null;
              const cancRec = cancs.find((c) => !c.recuperado && c.id && c.item_id);
              // Cada item cancelado se recupera sozinho (Douglas 26/10/2026, #P2610190031:
              // o botão pegava sempre o 1º cancelamento, um Claro Monitor, e o bloco não voltava).
              const cancsAbertos = [...new Map(
                cancs.filter((c) => !c.recuperado && c.id && c.item_id).map((c) => [c.item_id, c])
              ).values()];
              const itemDoCanc = (c: CancelamentoBanner) => ((pedido?.itens || []) as ItemBanner[]).find((i) => i.id === c.item_id);
              const cancEscolhido = cancsAbertos.find((c) => c.id === recuperarCancId);
              const produtosCancelados = [...new Set(itensCancelados.map((i) => i.produto || i.plano_nome).filter(Boolean))].join(', ');
              const aberto = todosCancelados || cancelamentoAberto;

              return (
                <div className={cn(
                  "rounded-xl border border-[#E4DDD3] bg-[#FAF8F5] dark:border-[#332727] dark:bg-card border-l-[3px] pl-[18px] pr-4",
                  todosCancelados ? "border-l-[#5B0A0D] dark:border-l-[#C87484] py-3.5" : "border-l-[#9D4459] dark:border-l-[#9D4459] py-2.5"
                )}>
                  <div className="flex flex-wrap items-center justify-between gap-2.5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F2E6E6] text-[#5B0A0D] dark:bg-[#2A1F1F] dark:text-[#C87484]">
                        <XCircle className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#5B0A0D] dark:text-[#C87484]">
                          {todosCancelados ? 'Pedido cancelado' : `${itensCancelados.length} ${itensCancelados.length === 1 ? 'item cancelado' : 'itens cancelados'} neste pedido`}
                        </p>
                        <p className="text-[11px] text-[#6B5A4D] dark:text-[#B5A8A8] truncate">
                          {todosCancelados
                            ? `${itensCancelados.length} de ${(pedido?.itens || []).length} ${itensCancelados.length === 1 ? 'item' : 'itens'}`
                            : produtosCancelados}
                          {ondeMorreu && ` · estava em ${ondeMorreu}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {motivosUnicos.map((titulo, i) => (
                        <span key={i} className="rounded-full border border-[#D9B8BF] bg-[#F7EEEF] px-2.5 py-0.5 text-[11px] font-medium text-[#7A2235] dark:border-[#5E2229] dark:bg-[#2A1F1F] dark:text-[#C87484]">
                          {titulo}
                        </span>
                      ))}
                      {!todosCancelados && (
                        <button type="button" onClick={() => setCancelamentoAberto((v) => !v)} className="flex items-center gap-1 text-[11px] text-[#6B5A4D] hover:text-[#5B0A0D] dark:text-[#B5A8A8] dark:hover:text-[#C87484]">
                          {aberto ? 'Recolher' : 'Ver justificativa'}
                          <ChevronDown className={cn("h-3 w-3 transition-transform", aberto && "rotate-180")} />
                        </button>
                      )}
                    </div>
                  </div>

                  {aberto && cancComObs && (
                    <div className="mt-3 rounded-lg border border-[#EDE8E0] bg-white px-3 py-2.5 dark:border-[#332727] dark:bg-[#241A1A]">
                      <p className="mb-1 flex items-center gap-1.5 text-[11px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                        <MessageSquare className="h-3.5 w-3.5" />
                        Justificativa{autorObs ? ` · ${autorObs}` : ''}{quandoObs ? ` · ${quandoObs}` : ''}
                      </p>
                      <p className="text-[13px] leading-relaxed text-[#2E2019] dark:text-foreground">{cancComObs.observacao}</p>
                    </div>
                  )}

                  {aberto && (pedido.lead_origem_id || cancRec) && (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2.5">
                      <p className="text-[11px] text-[#6B5A4D] dark:text-[#B5A8A8]">{recuperarBloqueado ? 'A proposta já foi corrigida: use Revisar Proposta para levar a correção ao item' : 'O que fazer: ajuste a proposta ou justifique para voltar ao fluxo'}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        {!recuperarBloqueado && cancsAbertos.map((c) => {
                          const it = itemDoCanc(c);
                          const nome = [it?.produto || it?.plano_nome, it?.seq != null ? `#${it.seq}` : null].filter(Boolean).join(' ');
                          return (
                            <Button key={c.id} variant="outline" size="sm" className="h-[29px] border-[#E4DDD3] bg-[#FAF8F5] text-xs text-[#5B0A0D] hover:bg-[#F4F0EA] hover:text-[#5B0A0D] dark:border-[#332727] dark:bg-transparent dark:text-[#C87484] dark:hover:bg-[#2A1F1F]" onClick={() => setRecuperarCancId(c.id as string)} title="Recuperar só este item, sem revisar a proposta — justificar e devolver ao fluxo">
                              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                              {cancsAbertos.length > 1 && nome ? `Recuperar ${nome}` : 'Recuperar (justificar)'}
                            </Button>
                          );
                        })}
                        {pedido.lead_origem_id && (
                          <BotaoRevisarProposta
                            pedido={{ id: pedido.id, lead_origem_id: pedido.lead_origem_id, quadro_atual: 'PEDIDO_CANCELADO' }}
                            isCancelado={true}
                            className="h-[29px] border-transparent bg-[linear-gradient(150deg,#4A1320,#5B0A0D)] text-xs font-bold text-white hover:text-white hover:opacity-95 dark:bg-[linear-gradient(150deg,#9D2530,#6C2236)]"
                            onRevisaoIniciada={(resolvedId?: string) => {
                              onOpenChange(false);
                              navigate(`/comercial?tab=prospeccao&lead=${pedido.lead_origem_id}&fromPedido=${resolvedId || pedido.id}`);
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {cancEscolhido && (
                    <RecuperarItemModal open onOpenChange={(o) => { if (!o) setRecuperarCancId(null); }} cancelamentoId={cancEscolhido.id as string} itemId={cancEscolhido.item_id as string} motivoTitulo={cancEscolhido.motivo?.titulo} dataLimite={cancEscolhido.data_limite_recuperacao ?? undefined} />
                  )}
                </div>
              );
            })()}

            {/* Validação de convergência pelo BKO (auto-declarada pelo consultor) */}
            <ConvergenciaValidacaoBko pedidoId={pedidoId} cnpj={(pedido as any)?.cnpj} cpf={(pedido as any)?.cpf} itens={pedido?.itens} />

            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className={`grid h-10 w-full rounded-xl border border-primary/10 bg-white p-1 shadow-sm dark:bg-card ${pedido.lead_origem_id ? 'grid-cols-4' : 'grid-cols-3'}`}>
                <TabsTrigger value="itens">
                  Itens
                  {pedido.qtd_produtos === 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs">0</Badge>
                  )}
                </TabsTrigger>
                {pedido.lead_origem_id && (
                  <TabsTrigger value="proposta" className="gap-1">
                    <FileText className="h-3 w-3" />
                    Proposta
                  </TabsTrigger>
                )}
                <TabsTrigger value="anexos">Anexos</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
              </TabsList>

              <TabsContent value="itens" className="space-y-4">

                <PedidoCard 
                  pedidoId={pedidoId} 
                  variant="full" 
                  showActions={false}
                  onEditItem={(item) => {
                    setEditingItem(item);
                    setEditItemModalOpen(true);
                  }}
                />
                
                {/* FATURAMENTO LADO A LADO, TOTAL EM DESTAQUE — Douglas,
                    17/09/2026: "ire para esta ocupando muito espaço um abaixo
                    do outro, coloque ao lado cada uma... mesma coisa o
                    faturamento. total coloque destaque."
                    Antes só existia um "Total do Pedido" no FIM da lista de
                    itens, em 13px — e ele sumia quando o pedido tinha um item
                    só. O operador precisa do total antes de ler os itens, não
                    depois, e Venda Nova x Migração é a leitura que o comercial
                    faz todo dia. O TOTAL leva borda bordô e mais largura: é o
                    número que decide. */}
                <div className="flex items-stretch gap-2">
                  {([
                    ['Venda nova', pedido?.total_venda_nova],
                    ['Migração', pedido?.total_migracao],
                  ] as const).map(([rotulo, valor]) => (
                    <div key={rotulo} className="flex-1 rounded-[9px] border border-border-warm bg-card px-3 py-2">
                      <div className="text-[9px] font-bold uppercase tracking-[1px] text-muted-foreground">{rotulo}</div>
                      <div className="font-display text-[15px] tabular-nums text-foreground">
                        {(valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </div>
                    </div>
                  ))}
                  <div className="flex-[1.15] rounded-[9px] border border-primary/45 bg-primary/[0.04] px-3 py-2">
                    <div className="text-[9px] font-bold uppercase tracking-[1px] text-primary/80">Total</div>
                    <div className="font-display text-[15px] font-semibold tabular-nums text-primary">
                      {(pedido?.total_faturamento ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </div>
                  </div>
                </div>

                <ItensTab pedidoId={pedidoId} pedido={pedido} focusItemSeq={focusItemSeq} />
              </TabsContent>

              {pedido.lead_origem_id && (
                <TabsContent value="proposta" className="space-y-4">
                  <PropostaOrigemPedido leadOrigemId={pedido.lead_origem_id} pedidoId={pedidoId} />
                </TabsContent>
              )}

              <TabsContent value="anexos" className="space-y-4">
                <AnexosTab pedidoId={pedidoId} clienteId={clienteId} />
              </TabsContent>

              <TabsContent value="timeline" className="space-y-4">
                <TimelineUnificada
                  cnpj={pedido?.cnpj}
                  pedidoId={pedidoId}
                  clienteId={clienteId}
                />
              </TabsContent>
              </Tabs>

            </div>
        ) : !loading && !error && !pedido ? (
          <div className="flex flex-col items-center justify-center h-64 space-y-4 mt-6">
            <FileSearch className="h-16 w-16 text-muted-foreground" />
            <div className="text-center max-w-md">
              <h3 className="font-semibold text-lg">Pedido não encontrado</h3>
              <p className="text-sm text-muted-foreground mt-2">
                O pedido ainda está sendo carregado ou não existe.
              </p>
            </div>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </div>
        ) : null}


        {/* Modal para editar item */}
        {editingItem && (
          <EditarItemCompletoModal
            open={editItemModalOpen}
            onOpenChange={setEditItemModalOpen}
            item={editingItem}
            pedidoId={pedidoId}
          />
        )}
        </div>

        {pedido && (
          <div className="flex h-[34px] flex-shrink-0 items-center justify-center gap-1.5 border-t border-border-warm bg-card/40 text-[10px] text-muted-foreground">
            <kbd className="rounded border border-border bg-accent px-1.5 py-0.5 font-mono text-[9px]">Esc</kbd>
            <span>Fechar</span>
            <span className="text-muted-foreground/40">·</span>
            <kbd className="rounded border border-border bg-accent px-1.5 py-0.5 font-mono text-[9px]">Ctrl+I</kbd>
            <span>Itens</span>
            <span className="text-muted-foreground/40">·</span>
            <kbd className="rounded border border-border bg-accent px-1.5 py-0.5 font-mono text-[9px]">Ctrl+A</kbd>
            <span>Anexos</span>
          </div>
        )}
      </ContentComponent>
    </Sheet>

    {/*  Modal de Warning - salvamentos pendentes */}
    <AlertDialog open={showPendingWarning} onOpenChange={setShowPendingWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Salvamento em andamento</AlertDialogTitle>
          <AlertDialogDescription>
            Existem alterações não salvas. O que deseja fazer?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex gap-2 flex-col sm:flex-row">
          <Button 
            variant="outline" 
            onClick={() => setShowPendingWarning(false)}
            disabled={isFlushing}
          >
            Continuar editando
          </Button>
          <Button 
            onClick={handleFlushAndClose}
            disabled={isFlushing}
          >
            {isFlushing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar e fechar'
            )}
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleDiscardAndClose}
            disabled={isFlushing}
          >
            Sair sem salvar
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    {emailAberto && pedido?.cnpj && (
      <EmailClienteDialog open={emailAberto} onOpenChange={setEmailAberto} modo={{ tipo: 'novo', pedidoId, cnpj: keepOnlyDigits(pedido.cnpj) }} />
    )}
    </>
  );
});
